import React, { useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "../../lib/api";

export const WaitlistForm = ({ id = "waitlist", innerRef }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await joinWaitlist({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
      });
      setSuccess(true);
      toast.success("You're on the list. We'll be in touch.");
      setForm({ name: "", email: "", company: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id={id}
      ref={innerRef}
      data-testid="waitlist-form"
      className="relative py-24 md:py-36 border-t border-[#27272A] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[600px] bg-[#FF6B00]/5 blur-[120px] pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
        <span className="label-eyebrow">09 / Register Interest</span>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter mt-4">
          Be first to know when{" "}
          <span className="text-[#FF6B00]">AdjustaGland</span> launches.
        </h2>
        <p className="mt-5 text-[#A1A1AA] text-base md:text-lg max-w-xl mx-auto">
          Join the waiting list for priority access, trade pricing and early
          updates as we move toward UK launch.
        </p>

        {success ? (
          <div
            data-testid="waitlist-success"
            className="mt-12 p-8 md:p-10 border border-[#FF6B00]/40 bg-[#141414] rounded-sm text-left"
          >
            <CheckCircle2 className="w-10 h-10 text-[#FF6B00] mb-4" />
            <div className="font-display text-2xl md:text-3xl">You&apos;re on the list.</div>
            <p className="text-[#A1A1AA] mt-3 leading-relaxed">
              Thanks for registering your interest in AdjustaGland. We&apos;ll be in
              touch as soon as we have a launch date and trade pricing to share.
            </p>
            <button
              data-testid="waitlist-add-another-btn"
              onClick={() => setSuccess(false)}
              className="btn-secondary mt-6"
            >
              Register Another
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-12 p-6 md:p-10 border border-[#27272A] bg-[#141414] rounded-sm text-left grid gap-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-eyebrow block mb-2">Name *</label>
                <input
                  data-testid="waitlist-input-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF6B00] focus:outline-none rounded-sm px-4 py-3 text-white placeholder-[#52525B] transition-colors"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Email *</label>
                <input
                  data-testid="waitlist-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@company.co.uk"
                  className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF6B00] focus:outline-none rounded-sm px-4 py-3 text-white placeholder-[#52525B] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Company</label>
              <input
                data-testid="waitlist-input-company"
                type="text"
                value={form.company}
                onChange={update("company")}
                placeholder="Company / Trade business (optional)"
                className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF6B00] focus:outline-none rounded-sm px-4 py-3 text-white placeholder-[#52525B] transition-colors"
              />
            </div>

            <button
              data-testid="waitlist-submit-btn"
              type="submit"
              disabled={submitting}
              className="btn-primary mt-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  Register Interest <Send className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="font-mono-spec text-[10px] uppercase tracking-[0.22em] text-[#52525B] text-center mt-1">
              We respect your privacy · No spam · UK GDPR compliant
            </p>
          </form>
        )}
      </div>
    </section>
  );
};
